import React, {ReactElement, useContext, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {trackUserAuthClick} from '@vio/core/src/auth/modules/actions'
import {
  getAuthenticationFlow,
  getIsAuthenticated,
  getPasswordlessLoginSourceComponent,
  getSusiActive,
  selectAuthenticationError
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'

import {useBackToFreezeOverlay} from '../../../../dealFreeze/hooks/useBackToFreezeOverlay'
import {setEmailSignUpVisibility} from '../../../Email/slice'
import {getHasSusiInUrl} from '../../../hooks/useSUSIBottomSheet'
import {
  canHandOffEmailSignInToApp,
  handOffEmailSignInToApp
} from '../../handOffEmailSignInToApp'
import {Agreement, SignInButtons, SignInTitle} from './components'
import {SusiContext} from './SusiProvider'

interface Props {
  infoContext?: Record<string, unknown>
  TitleSlot?: ReactElement | null
  MainContentSlot?: ReactElement | null
  BottomContentSlot?: ReactElement | null
  skipCloseAnimation?: boolean
}

export const SusiBottomSheet = ({
  TitleSlot,
  MainContentSlot,
  BottomContentSlot,
  skipCloseAnimation
}: Props) => {
  const dispatch = useDispatch()
  const susiActive = useSelector(getSusiActive)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const flow = useSelector(getAuthenticationFlow)
  const sourceComponent = useSelector(getPasswordlessLoginSourceComponent)
  const isOpen = susiActive && !isAuthenticated
  const {errorMessage, errorType} = useSelector(selectAuthenticationError)
  const hasSusiInUrl = getHasSusiInUrl()
  const {canGoBack, goBack} = useBackToFreezeOverlay(
    isOpen && flow === AuthenticationFlows.DealFreeze
  )
  const {
    brand,
    handleCloseOverlay,
    handleLogin,
    updateSUSILocationState,
    trackSignInError,
    setSkipCloseAnimation
  } = useContext(SusiContext)

  useEffect(() => {
    if (isOpen) {
      setSkipCloseAnimation(false)
    }
  }, [isOpen, setSkipCloseAnimation])

  const handleOnClose = (options?: {
    isEmailHandoff?: boolean
    isFreezeOverlayHandoff?: boolean
  }) => {
    if (hasSusiInUrl) {
      updateSUSILocationState({active: false})
    }
    handleCloseOverlay(options)
  }

  const handleEmailLogin = (e: React.SyntheticEvent) => {
    e.preventDefault()
    const emailSourceComponent = (sourceComponent ??
      SourceComponentType.Login) as SourceComponentType

    dispatch(
      trackUserAuthClick({
        authenticationMethod: AuthenticationMethod.Email,
        sourceComponent: emailSourceComponent,
        authenticationFlow: flow as AuthenticationFlows
      })
    )

    if (canHandOffEmailSignInToApp()) {
      handleOnClose({isEmailHandoff: true})
      handOffEmailSignInToApp({
        brand,
        sourceComponent: emailSourceComponent,
        flow
      })
      return
    }

    dispatch(
      setEmailSignUpVisibility({
        isOpen: true,
        sourceComponent: emailSourceComponent,
        flow
      })
    )
    handleOnClose({isEmailHandoff: true})
  }

  const SignInTitleComponent = TitleSlot ?? <SignInTitle />
  const SignInMainContentComponent = MainContentSlot ?? null
  const SignInBottomContentComponent = BottomContentSlot ?? (
    <>
      <SignInButtons
        handleLogin={handleLogin}
        handleEmailLogin={handleEmailLogin}
        handleOnClose={handleOnClose}
      />
      <Agreement />
    </>
  )

  const SignInStep = {
    TitleComponent: <div>{SignInTitleComponent}</div>,
    MainContentComponent: SignInMainContentComponent ? (
      <Box paddingBottom="s600">{SignInMainContentComponent}</Box>
    ) : null,
    BottomContentComponent: <div>{SignInBottomContentComponent}</div>
  }

  const {TitleComponent, MainContentComponent, BottomContentComponent} =
    SignInStep

  useEffect(() => {
    if (errorMessage && errorType) {
      trackSignInError({
        errorMessage,
        errorType
      })
    }
  }, [errorMessage, errorType, trackSignInError])

  return (
    <BottomSheet
      isOpen={isOpen}
      navBarProps={{
        variant: 'transparent',
        floating: true,
        startContent: canGoBack && (
          <Button
            variant="ghost"
            dataId="SusiBackToFreezeOverlay"
            iconStart={<Icon size="md" name="ChevronLeft" hasMirror />}
            onClick={() =>
              goBack(() => handleOnClose({isFreezeOverlayHandoff: true}))
            }
          />
        )
      }}
      onClose={() => handleOnClose()}
      // Skip close animation before navigation so BF-cache snapshot is clean
      prefersReducedMotion={skipCloseAnimation}
    >
      <Box paddingTop="s600">{TitleComponent}</Box>
      <Box paddingTop="s600">
        {MainContentComponent}
        {BottomContentComponent}
      </Box>
    </BottomSheet>
  )
}
